import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

/**
 * Public standalone page at /terms and /privacy. Reads the currently-
 * published version from the backend so a new version publish goes live
 * without a code deploy. Renders the stored markdown body verbatim.
 *
 * Also used inside the post-login LegalGate modal (with `embedded` prop)
 * so users see the same document in either place — no divergence between
 * "what the modal showed" and "what /terms serves".
 */
export function LegalDocumentPage({ kind: kindProp, embedded = false }) {
  const params = useParams();
  const kind = kindProp || params.kind;  // "terms" or "privacy"
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setDoc(null); setErr("");
    api.get(`/legal/${kind}`)
      .then(({ data }) => { if (!cancelled) setDoc(data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [kind]);

  const title = kind === "privacy" ? "Privacy Policy" : "Terms of Service";

  const body = (
    <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:text-flag-navy prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-a:text-flag-red prose-strong:text-flag-navy" data-testid={`legal-body-${kind}`}>
      {err && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">{err}</div>}
      {!doc && !err && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading {title}…
        </div>
      )}
      {doc && (
        <>
          <div className="text-xs text-slate-500 mb-2" data-testid={`legal-meta-${kind}`}>
            Version <strong>{doc.version}</strong> · effective {doc.effective_date}
          </div>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body_markdown}</ReactMarkdown>
        </>
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-flag-navy mb-4" data-testid={`legal-back-${kind}`}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-10">
          {body}
        </div>
        <div className="mt-6 text-center text-xs text-slate-400">
          © Electable LLC. Also see the{" "}
          <Link to={kind === "terms" ? "/privacy" : "/terms"} className="text-flag-red hover:underline">
            {kind === "terms" ? "Privacy Policy" : "Terms of Service"}
          </Link>.
          <div className="mt-2" data-testid="legal-party-disclaimer">
            Electable LLC is an independent company. It is not affiliated with, endorsed by, or authorized by the Republican National Committee or any party committee.
          </div>
        </div>
      </div>
    </div>
  );
}

export default LegalDocumentPage;
